import { useState, useTransition } from "react"
import { usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Heart } from "lucide-react"
import { cn } from "@/lib/utils"
import { votes } from "@/services/votes"
import type { SharedProps } from "@/types"

type Props = {
  sampleId: number
  voted: boolean
  votesCount: number
  size?: "sm" | "default"
}

export function VoteButton({ sampleId, voted: initialVoted, votesCount: initialCount, size = "default" }: Props) {
  const { currentUser } = usePage<SharedProps>().props
  const [voted, setVoted] = useState(initialVoted)
  const [count, setCount] = useState(initialCount)
  const [isPending, startTransition] = useTransition()

  const handleVote = () => {
    if (!currentUser) return

    // Optimistic update
    const wasVoted = voted
    const prevCount = count
    setVoted(!wasVoted)
    setCount(wasVoted ? count - 1 : count + 1)

    startTransition(async () => {
      try {
        const data = await votes.toggle(sampleId, wasVoted)
        setVoted(data.voted)
        setCount(data.votes_count)
      } catch {
        // Revert on error
        setVoted(wasVoted)
        setCount(prevCount)
      }
    })
  }

  if (!currentUser) {
    return (
      <div className={cn(
        "flex items-center gap-1 text-muted-foreground",
        size === "sm" ? "text-xs" : "text-sm"
      )}>
        <Heart className={size === "sm" ? "h-3 w-3" : "h-4 w-4"} />
        <span className="font-mono">{count}</span>
      </div>
    )
  }

  return (
    <Button
      variant="ghost"
      size={size === "sm" ? "sm" : "default"}
      className={cn(
        "gap-1 px-2",
        voted && "text-red-500 hover:text-red-600"
      )}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        handleVote()
      }}
      disabled={isPending}
    >
      <Heart
        className={cn(
          size === "sm" ? "h-3 w-3" : "h-4 w-4",
          voted && "fill-current"
        )}
      />
      <span className="font-mono">{count}</span>
    </Button>
  )
}
